import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './lib/auth';
import { AppShell } from './components/layout/AppShell';
import { LoginPage } from './pages/LoginPage';
import { EquipmentPage } from './pages/EquipmentPage';
import { PartsPage } from './pages/PartsPage';
import { SchedulesPage } from './pages/SchedulesPage';
import { WorkOrdersPage } from './pages/WorkOrdersPage';
import { WorkOrderDetailPage } from './pages/WorkOrderDetailPage';
import { HistoryPage } from './pages/HistoryPage';
import { UsersPage } from './pages/UsersPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { AuditPage } from './pages/AuditPage';
import { DashboardPage } from './pages/DashboardPage';
import { ReportsPage } from './pages/ReportsPage';
import { EquipmentDetailPage } from './pages/EquipmentDetailPage';
import { PartDetailPage } from './pages/PartDetailPage';
import { ProfilePage } from './pages/ProfilePage';
import { RegisterPage } from './pages/RegisterPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { LicenseExpiredPage } from './pages/LicenseExpiredPage';
import { LicensePage } from './pages/LicensePage';
import { BillingSettingsPage } from './pages/BillingSettingsPage';
import { MaintenanceSettingsPage } from './pages/MaintenanceSettingsPage';
import { VendorLoginPage }  from './pages/vendor/VendorLoginPage';
import { VendorTotpPage }   from './pages/vendor/VendorTotpPage';
import { VendorDashboard }  from './pages/vendor/VendorDashboard';

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function RequireSupervisor({ children }: { children: React.ReactNode }) {
  const { isSupervisor } = useAuth();
  if (!isSupervisor) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const { user, needsSetup } = useAuth();

  // Fresh install — no users in DB yet.
  // /register and /login are both accessible so the user can complete setup then sign in.
  // After login(), the login callback calls setNeedsSetup(false) and the full app renders.
  if (needsSetup) {
    return (
      <Routes>
        <Route path="/register"         element={<RegisterPage />} />
        <Route path="/login"            element={<LoginPage />} />
        <Route path="/license-expired"  element={<LicenseExpiredPage />} />
        <Route path="/vendor/login"     element={<VendorLoginPage />} />
        <Route path="/vendor/totp"      element={<VendorTotpPage />} />
        <Route path="/vendor"           element={<VendorDashboard />} />
        <Route path="*"                 element={<Navigate to="/register" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/vendor/login"     element={<VendorLoginPage />} />
      <Route path="/vendor/totp"      element={<VendorTotpPage />} />
      <Route path="/vendor"           element={<VendorDashboard />} />
      <Route path="/license-expired" element={<LicenseExpiredPage />} />
      <Route path="/settings/billing" element={
        <RequireAuth><BillingSettingsPage /></RequireAuth>
      } />
      <Route path="/login"           element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/register"        element={user ? <Navigate to="/dashboard" replace /> : <RegisterPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />} />
      <Route path="/reset-password"  element={<ResetPasswordPage />} />
      <Route path="/verify-email"    element={<VerifyEmailPage />} />

      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard"     element={<DashboardPage />} />
        <Route path="/equipment"     element={<EquipmentPage />} />
        <Route path="/equipment/:id" element={<EquipmentDetailPage />} />
        <Route path="/parts"         element={<PartsPage />} />
        <Route path="/parts/:id"     element={<PartDetailPage />} />
        <Route path="/schedules"     element={<SchedulesPage />} />
        <Route path="/work-orders"      element={<WorkOrdersPage />} />
        <Route path="/work-orders/:id"  element={<WorkOrderDetailPage />} />
        <Route path="/history"       element={<HistoryPage />} />
        <Route path="/reports"       element={<ReportsPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/profile"       element={<ProfilePage />} />

        <Route path="/users" element={
          <RequireAdmin><UsersPage /></RequireAdmin>
        } />
        <Route path="/audit" element={
          <RequireSupervisor><AuditPage /></RequireSupervisor>
        } />
        <Route path="/settings/license" element={
          <RequireAdmin><LicensePage /></RequireAdmin>
        } />
        <Route path="/settings/maintenance" element={<MaintenanceSettingsPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
